// Customer profile - the fines tab's rows, built from the engine's own per-fine figures (audit DUP-17 / CON-23).
//
// The engine owns customer money: `charge` (the fine + its impound as billed, 0 when the customer paid RTA
// himself) and `owed_not_on_ejar` (what Ejar does not carry yet - the one rule of the fines page and The
// Engine). The profile used to call a fine "Paid" when its CONTRACT's status said paid ("Delivered - Paid"),
// whatever Ejar held, and looked at the first 30 contracts only.

const key = (v) => String(v === null || v === undefined ? '' : v).trim().toUpperCase();
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/** Where the fine stands against Ejar: 'customer' (paid at RTA by the customer, never billed), 'off' (still owed), 'on'. */
export function ejarState(f) {
    if (String(f?.paid_by || '') === 'customer') return 'customer';
    return num(f?.owed_not_on_ejar) > 0.005 ? 'off' : 'on';
}

/**
 * The fines of every contract of the customer (no cap), current (open-contract) fines first, then newest.
 * `forContract(contractNo)` -> the engine's fine rows of that contract (finesIndex.forContract).
 */
export function profileFines(contractRows, forContract, formatPlate = (p) => p) {
    const rows = contractRows || [];
    const contracts = [...new Set(rows.map((r) => key(r.c)).filter(Boolean))];
    const openSet = new Set(rows.filter((r) => String(r.s || '').trim().toLowerCase() === 'open').map((r) => key(r.c)));
    const seen = new Set();
    const out = [];
    contracts.forEach((cn) => {
        (forContract(cn) || []).forEach((f) => {
            const ticket = String(f.ticket_number || '');
            if (ticket && seen.has(ticket)) return;
            if (ticket) seen.add(ticket);
            const contractNo = key(f.contract_no || cn);
            const state = ejarState(f);
            out.push({
                ticket,
                plate: formatPlate(f.plate_number || ''),
                date: f.fine_date || '',
                contractNo,
                onOpen: openSet.has(contractNo),
                rtaStatus: f.status || '',
                ejar: state,
                // the engine's charge; an engine without it yet gives the billed fine, as before
                charge: state === 'customer' ? 0 : num(f.charge ?? f.billed_amount ?? f.amount),
                owed: state === 'customer' ? 0 : num(f.owed_not_on_ejar),
            });
        });
    });
    return out.sort((a, b) => (Number(b.onOpen) - Number(a.onOpen)) || String(b.date).localeCompare(String(a.date)));
}

/** What the customer still owes on fines Ejar does not carry yet (the header figure). */
export const totalOwed = (fines) => Math.round((fines || []).reduce((s, f) => s + num(f.owed), 0) * 100) / 100;
